'use client';

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/** Thin blue arc spinner — same visual as Attendance page (system default). */
export function AttendanceStyleSpinner({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto',
        className
      )}
      aria-hidden
    />
  );
}

/**
 * Full-page or in-layout loading state (unified with Attendance).
 * @param fullScreen — viewport-centered splash (no sidebar).
 * @param centerInLayout — center in main content below app chrome (sidebar + subscription banner).
 */
export function PageLoading({
  message,
  /** Resolve in-component from `shell` namespace (avoids frozen SSR strings from parents). */
  shellKey,
  fullScreen = false,
  centerInLayout = false,
  hideMessage = false,
  className,
}: {
  message?: string;
  shellKey?: string;
  fullScreen?: boolean;
  centerInLayout?: boolean;
  hideMessage?: boolean;
  className?: string;
}) {
  const { t, i18n } = useTranslation('common');
  const { t: tShell } = useTranslation('shell');
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const shellMessage = shellKey ? tShell(shellKey) : null;
  const displayMessage = hideMessage ? null : (shellMessage ?? message ?? t('loadingContent'));
  const showMessage = mounted && displayMessage;

  const outer = fullScreen
    ? 'min-h-screen flex items-center justify-center'
    : centerInLayout
      ? 'flex flex-col items-center justify-center w-full min-h-[calc(100vh-9rem)] py-0'
      : 'flex flex-col items-center justify-center py-16 w-full min-h-[400px]';

  return (
    <div className={cn(outer, className)}>
      <div className="text-center">
        <AttendanceStyleSpinner />
        {showMessage ? (
          <p key={i18n.language} className="mt-4 text-gray-600">
            {displayMessage}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Centered overlay for tables/cards while data is loading (spinner + label below). */
export function TableLoadingOverlay({
  message,
  className,
  compact = false,
}: {
  message?: string;
  className?: string;
  compact?: boolean;
}) {
  const { t, i18n } = useTranslation('common');
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const displayMessage = message ?? t('loadingTableData');
  const showMessage = mounted && displayMessage;

  return (
    <div
      className={cn(
        'absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background/70 backdrop-blur-[1px]',
        className
      )}
      aria-busy="true"
      aria-live="polite"
    >
      <div className={cn('text-center', compact && 'flex flex-col items-center gap-2')}>
        {compact ? (
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" aria-hidden />
        ) : (
          <AttendanceStyleSpinner className="h-24 w-24" />
        )}
        {showMessage ? (
          <p
            key={i18n.language}
            className={cn('text-sm text-gray-600', !compact && 'mt-4')}
          >
            {displayMessage}
          </p>
        ) : null}
      </div>
    </div>
  );
}

interface LoadingProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  className?: string;
}

/** Inline row spinner (tables, compact UI). Icon-based; not the large arc. */
export default function Loading({ size = 'md', text, className }: LoadingProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
  };

  return (
    <div className={cn('flex items-center justify-center space-x-2', className)}>
      <Loader2 className={cn('animate-spin', sizeClasses[size])} />
      {text && <span className="text-sm text-muted-foreground">{text}</span>}
    </div>
  );
}

export function LoadingSpinner({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
  };

  return <Loader2 className={cn('animate-spin', sizeClasses[size], className)} />;
}

export function LoadingPage({ message }: { message?: string }) {
  return <PageLoading message={message} fullScreen />;
}

export function LoadingCard({ message }: { message?: string }) {
  const { t } = useTranslation('common');
  const displayMessage = message ?? t('loadingContent');

  return (
    <div className="flex items-center justify-center p-8 border rounded-lg bg-card">
      <div className="text-center">
        <AttendanceStyleSpinner />
        {displayMessage ? (
          <p className="mt-4 text-gray-600 text-sm">{displayMessage}</p>
        ) : null}
      </div>
    </div>
  );
}
